import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { addQuestion, deleteOption, getQuestionById } from "../../../services/QuestionService";
import { useParams } from "react-router-dom";

/* ================= HELPERS ================= */
const createOption = () => ({
    tempId: `o-${Date.now()}-${Math.random()}`,
    option_text: "",
    is_correct: false,
});

const MIN_OPTIONS = 2;

/* ================= MAIN ================= */
const AddQuestion = () => {
    const [question, setQuestion] = useState("");
    const [description, setDescription] = useState("");
    const hasFetched = useRef(false);
    const { id } = useParams();

    // ✅ 4 options on first load
    const [options, setOptions] = useState(
        Array.from({ length: 4 }, () => createOption())
    );

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (id && !hasFetched.current) {
            hasFetched.current = true;
            const fetchQuestion = async () => {
                try {
                    setLoading(true);
                    const response = await getQuestionById(id);
                    const question = response?.data;
                    setQuestion(question?.data?.question);
                    setDescription(question?.data?.description);
                    let options = question?.data?.options.map(o => ({
                        ...o,
                        tempId: `o-${Date.now()}-${Math.random()}`,
                    }));
                    setOptions(options);
                } catch (err) {
                    console.error("Error fetching question:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchQuestion();
        }
    }, [id]);

    /* ================= OPTION HANDLERS ================= */
    const addOption = () => {
        setOptions((prev) => [...prev, createOption()]);
    };

    const deleteOptionHandler = async (option, index) => {
        // prevent removing below minimum
        if (options.length <= MIN_OPTIONS) {
            setErrors(prev => ({
                ...prev,
                options: "At least 2 options are required",
            }));
            return;
        }

        try {
            // API delete only if option already exists in DB
            if (option.id) {
                await deleteOption(option.id);
            }

            setOptions(prev => prev.filter((_, i) => i !== index));
        } catch (err) {
            Swal.fire("Error", "Failed to delete option", "error");
        }
    };

    const updateOptionText = (index, value) => {
        setOptions((prev) =>
            prev.map((o, i) =>
                i === index ? { ...o, option_text: value } : o
            )
        );
    };

    const selectCorrectOption = (index) => {
        setOptions((prev) =>
            prev.map((o, i) => ({
                ...o,
                is_correct: i === index,
            }))
        );
    };

    const validate = () => {
        const newErrors = {};

        if (!question.trim()) {
            newErrors.question = "Question title is required";
        }

        if (options.length < MIN_OPTIONS) {
            newErrors.options = "At least 2 options are required";
        }

        if (options.some((o) => !o.option_text.trim())) {
            newErrors.options = "All options must be filled";
        }

        const correctCount = options.filter((o) => o.is_correct).length;
        if (correctCount !== 1) {
            newErrors.correct = "Select exactly one correct option";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ================= SAVE ================= */
    const handleSave = async () => {

        if (!validate()) return;

        setLoading(true);

        try {
            let questionData = {
                question_id: id,
                question: question,
                description: description,
                options: options
            };

            const response = await addQuestion(questionData);
            if (response?.data?.success) {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Question added successfully",
                });
            }
        } catch (error) {
            Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageTitle
                activeMenu={`${id ? "Edit Question" : "Add Question"}`}
                motherMenu="Question"
                activeMenuLink={`${id ? `edit-question/${id}` : "add-question"}`}
                motherMenuLink="questions"
            />

            <div className="card">
                <div className="card-body">
                    {/* Question */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">
                            Question Title
                        </label>
                        <input
                            className="form-control"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                        />
                        {errors.question && (
                            <div className="text-danger small mt-1">
                                {errors.question}
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">
                            Question Description
                        </label>
                        <input
                            className="form-control"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    {/* Options */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">
                            Question Options
                        </label>

                        {options.map((opt, index) => (
                            <div
                                key={opt.tempId}
                                className="d-flex gap-2 align-items-center mb-2"
                            >
                                {/* Correct */}
                                <input
                                    type="radio"
                                    checked={opt.is_correct}
                                    onChange={() => selectCorrectOption(index)}
                                />

                                {/* Text */}
                                <input
                                    className="form-control"
                                    placeholder={`Option ${index + 1}`}
                                    value={opt.option_text}
                                    onChange={(e) =>
                                        updateOptionText(index, e.target.value)
                                    }
                                />

                                {/* Remove */}
                                {options.length > MIN_OPTIONS && (
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => deleteOptionHandler(opt, index)}
                                    >
                                        ✕
                                    </Button>
                                )}
                            </div>
                        ))}
                        {errors.options && (
                            <div className="text-danger small mt-1">
                                {errors.options}
                            </div>
                        )}

                        {errors.correct && (
                            <div className="text-danger small mt-1">
                                {errors.correct}
                            </div>
                        )}
                    </div>

                    <Button size="sm" variant="secondary" onClick={addOption}>
                        + Add Option
                    </Button>

                    <div className="text-end mt-4">
                        <Button onClick={handleSave} disabled={loading}>
                            {loading ? "Saving..." : "Save Question"}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddQuestion;
